import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import CbmContext from '../Context/Cbm/cbmContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { renderComponentByFormName } from '../Components/Utils/helperFuncs';
import {
  getFormDepartment,
  requiresManagerSignature,
  requiresEmployeeSignature,
} from '../config/formRegistry';
import { useFormManager } from '../hooks/useFormManager';
import SubmitBtn from '../Components/SubmitButton/SubmitBtn';
import SigPad from '../Components/Signature/SigPad';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const [formName, setFormName] = useState(queryParams.get('formName'));
  const [showTerminationOptions, setShowTerminationOptions] = useState(false);

  const cbmContext = useContext(CbmContext);
  const {
    portalUser,
    auth,
    validateToken,
    formSubmit,
    loading,
    storeLoading,
    authLoading,
    errorCode,
    submitStatus,
    submitFailed,
    submitSuccess,
    getStores,
    stores,
    retrieveStores,
    resetSubmissionStates,
  } = cbmContext;

  // Use the form manager hook
  const {
    payload,
    setPayload,
    formError,
    setFormError,
    errMsg,
    setErrMsg,
    setIdBadgeFile,
    setGovIdFront,
    setGovIdRear,
    setSsnCard,
    managerPadRef,
    employeePadRef,
    clearManagerPad,
    clearEmployeePad,
    buildFinalPayload,
    canSubmit,
  } = useFormManager(formName, portalUser);

  const validationCheck = useCallback(() => {
    if (formError) {
      return false;
    }

    // Check for backend/API errors - ErrorBanner handles displaying these,
    // so we don't set errMsg here to avoid duplicate messages
    if (
      errorCode !== '' &&
      errorCode !== null &&
      submitFailed &&
      submitStatus
    ) {
      return false;
    }

    return true;
  }, [errorCode, submitFailed, submitStatus, formError]);

  useEffect(() => {
    if (!auth || authLoading) return;

    // Always let the backend validate; axios interceptor will refresh on 401
    validateToken(auth);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const qpFormName = queryParams.get('formName');
    setFormName(qpFormName);
  }, [queryParams]);

  useEffect(() => {
    if (submitStatus && submitSuccess) {
      console.log('Navingating to success page');
      navigate('/success', { state: { payload: payload } });
    }
  }, [submitStatus, submitSuccess, navigate, payload]);

  useEffect(() => {
    if (!formError) {
      setErrMsg('');
    }
  }, [formError, setErrMsg]);

  useEffect(() => {
    if (!storeLoading) {
      if (portalUser && stores.length === 0) {
        if (portalUser?.district && retrieveStores !== false) {
          getStores(portalUser.district);
        }
      }
    }
    // eslint-disable-next-line
  }, [getStores, stores, portalUser?.district]);

  useEffect(() => {
    if (errorCode !== '' && errorCode !== null && submitFailed) {
      validationCheck();
    }
  }, [validationCheck, errorCode, submitFailed]);

  const onSubmit = async (e) => {
    e.preventDefault();

    // Reset previous submission states
    if (submitFailed || submitStatus || submitSuccess) {
      resetSubmissionStates();
    }

    // Check if form can be submitted
    if (!canSubmit()) {
      window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      return;
    }

    // Build the final payload with signatures/files
    const finalPayload = buildFinalPayload();

    if (!finalPayload) {
      window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      return;
    }

    // Final validation check
    if (validationCheck()) {
      setErrMsg('');
      if (submitStatus === false && formError === false) {
        await formSubmit(finalPayload, formName, getFormDepartment(formName));
      }
    } else {
      // Set error message to trigger inline validation display
      setErrMsg(
        'Please fill in all required fields correctly before submitting.'
      );
      window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  };
  return (
    <div className="container">
      {(loading || storeLoading || authLoading) && !submitFailed ? (
        <FontAwesomeIcon
          className="loading-spinner"
          icon={faSpinner}
          spin
          size="2xl"
        />
      ) : (
        <div className="formContainer">
          {/* Global ErrorBanner already shows user-friendly errors; avoid duplicate banner here */}
          {formName === null ? (
            <div className="home-landing">
              <div className="home-hero">
                <div className="hero-content">
                  <h1 className="hero-title">
                    Welcome, {portalUser?.userFirst} {portalUser?.userLast}
                  </h1>
                  <p className="hero-subtitle">
                    CBM Portal - Your central hub for managing forms and
                    submissions
                  </p>
                </div>
              </div>

              <div className="home-stats">
                <div className="stat-card">
                  <div className="stat-icon">📋</div>
                  <div className="stat-info">
                    <div className="stat-label">Available Forms</div>
                    <div className="stat-value">12</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✓</div>
                  <div className="stat-info">
                    <div className="stat-label">Quick Access</div>
                    <div className="stat-value">Ready</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🏪</div>
                  <div className="stat-info">
                    <div className="stat-label">
                      Your District
                      {Array.isArray(portalUser?.district) &&
                      portalUser.district.length > 1
                        ? 's'
                        : ''}
                    </div>
                    <div className="stat-value">
                      {(() => {
                        if (!portalUser?.district) return 'N/A';

                        // Handle array of districts
                        if (Array.isArray(portalUser.district)) {
                          if (portalUser.district.length === 0) return 'N/A';

                          const districts = portalUser.district
                            .map((d) =>
                              typeof d === 'object' ? d.districtName : d
                            )
                            .filter(Boolean);

                          // Single district - display normally
                          if (districts.length === 1) {
                            return districts[0];
                          }

                          // Multiple districts - display as bullet list
                          return (
                            <ul className="district-list">
                              {districts.map((district, idx) => (
                                <li key={idx}>{district}</li>
                              ))}
                            </ul>
                          );
                        }

                        // Handle single district object
                        if (typeof portalUser.district === 'object') {
                          return portalUser.district.districtName || 'N/A';
                        }

                        // Handle string district
                        return portalUser.district;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="home-forms-section">
                <h2 className="section-title">Available Forms</h2>
                <p className="section-subtitle">Select a form to get started</p>

                <div className="forms-grid">
                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=mileage')}
                  >
                    <div className="form-card-icon">🚗</div>
                    <h3 className="form-card-title">Mileage</h3>
                    <p className="form-card-desc">
                      Submit mileage reimbursement
                    </p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=nonMnMileage')}
                  >
                    <div className="form-card-icon">🗺️</div>
                    <h3 className="form-card-title">Non-MN Mileage</h3>
                    <p className="form-card-desc">Out of state mileage</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=perDiem')}
                  >
                    <div className="form-card-icon">🍽️</div>
                    <h3 className="form-card-title">Per Diem</h3>
                    <p className="form-card-desc">Daily allowance request</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=hotel')}
                  >
                    <div className="form-card-icon">🏨</div>
                    <h3 className="form-card-title">Hotel</h3>
                    <p className="form-card-desc">Lodging reimbursement</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=pto')}
                  >
                    <div className="form-card-icon">🌴</div>
                    <h3 className="form-card-title">PTO</h3>
                    <p className="form-card-desc">Time off request</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=timeAdjustment')}
                  >
                    <div className="form-card-icon">⏰</div>
                    <h3 className="form-card-title">Time Adjustment</h3>
                    <p className="form-card-desc">Correct time entries</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=backPay')}
                  >
                    <div className="form-card-icon">💰</div>
                    <h3 className="form-card-title">Back Pay</h3>
                    <p className="form-card-desc">Payment adjustment</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=bonus')}
                  >
                    <div className="form-card-icon">🎁</div>
                    <h3 className="form-card-title">Bonus</h3>
                    <p className="form-card-desc">Bonus payment request</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=uniform')}
                  >
                    <div className="form-card-icon">👕</div>
                    <h3 className="form-card-title">Uniform</h3>
                    <p className="form-card-desc">Uniform request</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=targetOrder')}
                  >
                    <div className="form-card-icon">🎯</div>
                    <h3 className="form-card-title">Target Order</h3>
                    <p className="form-card-desc">Target purchase order</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=newHire')}
                  >
                    <div className="form-card-icon">👤</div>
                    <h3 className="form-card-title">New Hire</h3>
                    <p className="form-card-desc">New employee form</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div
                    className="form-card"
                    onClick={() => navigate('/?formName=workTicket')}
                  >
                    <div className="form-card-icon">🎫</div>
                    <h3 className="form-card-title">Work Ticket</h3>
                    <p className="form-card-desc">Submit work ticket</p>
                    <div className="form-card-arrow">→</div>
                  </div>

                  <div className="form-card termination-card">
                    <div
                      className="form-card-main"
                      onClick={() => setShowTerminationOptions((prev) => !prev)}
                    >
                      <div className="form-card-icon">⚠️</div>
                      <h3 className="form-card-title">Termination</h3>
                      <p className="form-card-desc">
                        Choose a termination form type
                      </p>
                      <div className="form-card-arrow">▼</div>
                    </div>

                    {showTerminationOptions && (
                      <div className="termination-options">
                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem('termType', 'quit');
                            navigate('/?formName=termination&termForm=quit');
                          }}
                        >
                          Quit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem('termType', 'noCall');
                            navigate('/?formName=termination&termForm=noCall');
                          }}
                        >
                          No Call/No Show
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem('termType', 'noShowFirstDay');
                            navigate(
                              '/?formName=termination&termForm=noShowFirstDay'
                            );
                          }}
                        >
                          No Show First Day
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem('termType', 'Other');
                            navigate('/?formName=termination&termForm=Other');
                          }}
                        >
                          Other Reason
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="home-help-section">
                <div className="help-card">
                  <h3>Need Help?</h3>
                  <p>
                    If you have questions or need assistance, please contact
                    your manager or the HR department.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <Fragment>
              {/* Removed generic errMsg display - ValidationErrors component in forms provides detailed feedback */}
              {renderComponentByFormName(
                formName,
                payload,
                setPayload,
                submitSuccess,
                submitFailed,
                submitSuccess,
                setIdBadgeFile,
                setGovIdFront,
                setGovIdRear,
                setSsnCard,
                formError,
                setFormError,
                errMsg
              )}
            </Fragment>
          )}
          {requiresEmployeeSignature(formName) && (
            <fieldset className="employeeSig">
              <legend>Employee Signature</legend>
              <div className="signature">
                <SigPad sigPad={employeePadRef} clearPad={clearEmployeePad} />
              </div>
            </fieldset>
          )}
          {requiresManagerSignature(formName) && (
            <fieldset className="employeeSig">
              <legend>Manager Signature</legend>
              <div className="signature">
                <SigPad sigPad={managerPadRef} clearPad={clearManagerPad} />
              </div>
            </fieldset>
          )}
          {formName !== null && (
            <div style={{ width: '100%', marginTop: '1rem' }}>
              <SubmitBtn onSubmit={onSubmit} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
